import { Document, Page, Text, View } from '@react-pdf/renderer'
import { PdfPreparedFor, tw } from '~/components/pdfs'
import { cn, fmtNumberAbbr } from '~/utils'
import {
  CompChart,
  CompStats,
  CompTable,
  PdfBrokerFooter,
  type CompChartConfig,
  type CompStatsConfig,
  type CompTableConfig,
} from './components'
import { PdfDisclaimersPage, ReportPdfTitlePage } from './pages'
import type { ReportPdfSwitchProps } from './ReportPdfSwitch'
import { getLeaseSpacesData, getLeaseSpacesStatsData } from './utils'

// Lease Comp Chart Configuration Function
const getLeaseChartConfig = (): CompChartConfig => {
  return {
    bars: [
      {
        dataKey: 'averageRate',
        fill: '#a78bfa',
        name: 'Avg. Rate SF/Yr',
      },
    ],
    yAxisWidth: 70,
    yAxisFormatter: (value: number) => `$${value}/SF/Yr`,
  }
}

// Lease Comp Stats Configuration Function
const getLeaseStatsConfig = (isLandReport: boolean): CompStatsConfig[] => {
  const statsConfig: CompStatsConfig[] = [
    {
      key: 'averageRate',
      label: 'Avg. Rate',
      formatter: (value: number) =>
        // TODO Change to sf/year for land
        `$${(Math.round(value * 100) / 100).toLocaleString()} ${
          isLandReport ? 'ac/yr' : 'SF/Yr'
        }`,
    },
  ]

  if (isLandReport) {
    statsConfig.push({
      key: 'averageAcres',
      label: 'Avg. Acres',
      formatter: (value: number) =>
        value > 100
          ? `${Math.round(value).toLocaleString()} ac`
          : `${value.toFixed(2)} ac`,
    })
  } else {
    statsConfig.push({
      key: 'averageAvailableSf',
      label: 'Avg. Available SF',
      formatter: (value: number) => `${Math.round(value).toLocaleString()} SF`,
    })
  }

  statsConfig.push({
    key: 'totalSpaces',
    label: 'Total Spaces',
    formatter: (value: number) => `${value}`,
  })

  return statsConfig
}

// Lease Comp Table Configuration Function
const getLeaseCompTableConfig = (isLandReport: boolean): CompTableConfig => {
  // TODO Add links to address
  const baseColumns = [
    {
      key: 'address',
      label: 'Address',
    },
    {
      key: 'spaceName',
      label: 'Space Name',
      basis: 3,
    },
  ]

  const landReportColumns = [
    {
      key: 'minRate',
      label: 'Min Rate',
      formatter: (value: number) =>
        value ? `$${value.toLocaleString()} SF/Yr` : '-',
    },
    {
      key: 'maxRate',
      label: 'Max Rate',
      formatter: (value: number) =>
        value ? `$${value.toLocaleString()} SF/Yr` : '-',
    },
    {
      key: 'acres',
      label: 'Acres',
      formatter: (value: number) =>
        value ? `${value.toLocaleString()} ac` : '-',
    },
    {
      key: 'lotSizeSf',
      label: 'Lot Size SF',
      formatter: (value: number) =>
        value ? `${fmtNumberAbbr(value)} SF` : '-',
    },
  ]

  const buildingReportColumns = [
    {
      key: 'minRate',
      label: 'Min Rate',
      formatter: (value: any) =>
        value ? `$${(value as number).toLocaleString()} SF/Yr` : '-',
    },
    {
      key: 'maxRate',
      label: 'Max Rate',
      formatter: (value: any) =>
        value ? `$${(value as number).toLocaleString()} SF/Yr` : '-',
    },
    {
      key: 'minAvailableSf',
      label: 'Min SF',
      formatter: (value: any) =>
        value ? `${(value as number).toLocaleString()} SF` : '-',
    },
    {
      key: 'maxAvailableSf',
      label: 'Max SF',
      formatter: (value: any) =>
        value ? `${(value as number).toLocaleString()} SF` : '-',
    },
  ]

  const endColumns = [
    {
      key: 'date',
      label: 'Avail Date',
    },
  ]

  return {
    columns: [
      ...baseColumns,
      ...(isLandReport ? landReportColumns : buildingReportColumns),
      ...endColumns,
    ],
  }
}

export default function ReportLeaseCompPdf({
  organization,
  filters,
  title,
  user,
  company,
  brokers,
  listings,
  pdfOptions,
}: ReportPdfSwitchProps) {
  // Get lease spaces from lease and saleLease listings
  const leaseSpaces = getLeaseSpacesData(listings, false, filters)

  // Determine if this is a land report based on lease spaces
  const isLandReport =
    leaseSpaces.filter((space) => space.propertyType === 'Land' || space.acres)
      .length >=
    leaseSpaces.length - Math.round(leaseSpaces.length / 4)

  // Re-process data with correct land report determination
  const leaseData = getLeaseSpacesData(listings, isLandReport, filters)

  return (
    <Document title={title}>
      {pdfOptions.showTitlePage && (
        <ReportPdfTitlePage
          title={title}
          company={company}
          user={user}
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
        />
      )}
      {leaseData.length > 0 && (
        <Page
          size="LETTER"
          style={tw('flex flex-col gap-4 px-8 pb-24 pt-8 font-sans text-sm')}
          orientation="portrait">
          <View
            style={tw(
              cn(
                !pdfOptions.showTitlePage &&
                  'flex flex-row justify-between gap-6'
              )
            )}>
            {!pdfOptions.showTitlePage && (
              <View style={tw('mb-3')}>
                <Text style={tw('font-heading mb-2 text-2xl leading-tight')}>
                  {title}
                </Text>
                <PdfPreparedFor />
              </View>
            )}

            <View style={tw(cn(!pdfOptions.showTitlePage && 'text-right'))}>
              <Text style={tw('text-xl font-bold')}>
                {leaseData.length} Available Spaces
              </Text>
            </View>
          </View>

          <CompChart data={leaseData} config={getLeaseChartConfig()} />

          <CompStats
            statsData={getLeaseSpacesStatsData(listings, isLandReport, filters)}
            config={getLeaseStatsConfig(isLandReport)}
          />

          <CompTable
            data={leaseData}
            config={getLeaseCompTableConfig(isLandReport)}
          />

          <PdfBrokerFooter
            organization={organization}
            user={user}
            company={company}
            brokers={brokers}
          />
        </Page>
      )}
      <PdfDisclaimersPage
        pdfOptions={pdfOptions}
        organization={organization}
        orientation="portrait"
      />
    </Document>
  )
}
